// Capa de red P2P (WebRTC vía PeerJS) para jugar con amigos.
// Modelo: el ANFITRIÓN es autoritario. Los invitados envían intenciones y
// reciben el estado. Máximo 3 jugadores en total (anfitrión + 2 invitados).
// Esta capa es agnóstica del juego: solo gestiona sala, presencia y mensajes.
import Peer from 'peerjs';
import { EVENTS } from '../utils/constants.js';
import { getPeerOptions } from './peer-config.js';

export const MAX_PLAYERS = 2;
// Versión del PROTOCOLO de red. Súbela cuando cambie el formato del estado o los
// mensajes. Sirve para detectar dispositivos con una versión distinta (p. ej. uno
// con el bundle viejo en caché) y avisar en vez de fallar la sincronización en silencio.
export const PROTOCOL = 1;
const PREFIX = 'palabriks-';
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // sin caracteres ambiguos
const CODE_LEN = 5;

function randomCode() {
  let s = '';
  for (let i = 0; i < CODE_LEN; i++) s += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
  return s;
}

export class PeerNet {
  constructor(bus) {
    this.bus = bus;
    this.peer = null;
    this.isHost = false;
    this.code = null;
    this.selfId = null;
    this.selfName = 'Tú';
    // Conexiones: host -> Map(peerId->conn); guest -> una sola conn al host.
    this.conns = new Map();
    this.hostConn = null;
    // Roster de jugadores en la sala [{id, name, host}].
    this.roster = [];
    this._msgHandlers = new Set();
  }

  // --------------------------------------------------------------------------
  // Anfitrión
  // --------------------------------------------------------------------------
  /** Crea una sala. Reintenta con otro código si el id está ocupado. */
  async createRoom(name, { retries = 5, avatar = '👑' } = {}) {
    this.isHost = true;
    this.selfName = name || 'Anfitrión';
    this.selfAvatar = avatar;
    const opts = await getPeerOptions(); // credenciales TURN (Cloudflare) listas antes de conectar
    return new Promise((resolve, reject) => {
      const attempt = (left) => {
        const code = randomCode();
        const peer = new Peer(PREFIX + code, { ...opts, debug: 1 });
        let settled = false;
        peer.on('open', () => {
          settled = true;
          this.peer = peer;
          this.code = code;
          this.selfId = peer.id;
          this.roster = [{ id: peer.id, name: this.selfName, host: true, avatar: this.selfAvatar }];
          this._bindHost();
          this._emitRoster();
          resolve({ code });
        });
        peer.on('error', (err) => {
          if (settled) { this._onError(err); return; }
          peer.destroy();
          if (err.type === 'unavailable-id' && left > 0) attempt(left - 1);
          else reject(err);
        });
      };
      attempt(retries);
    });
  }

  _bindHost() {
    this.peer.on('connection', (conn) => {
      // Rechaza si la sala está llena.
      if (this.roster.length >= MAX_PLAYERS) {
        conn.on('open', () => { conn.send({ t: 'full' }); setTimeout(() => conn.close(), 200); });
        return;
      }
      conn.on('open', () => {
        this.conns.set(conn.peer, conn);
      });
      conn.on('data', (msg) => this._onHostData(conn, msg));
      conn.on('close', () => this._onGuestGone(conn.peer));
      conn.on('error', (e) => this._onError(e));
    });
    this.peer.on('error', (e) => this._onError(e));
    this.peer.on('disconnected', () => { try { this.peer.reconnect(); } catch { /* noop */ } });
  }

  _onHostData(conn, msg) {
    if (!msg || !msg.t) return;
    if (msg.t === 'hello') {
      // Versión distinta (uno de los dos con el bundle viejo en caché): avisa y cierra.
      if (msg.proto !== PROTOCOL) {
        conn.send({ t: 'incompatible' });
        setTimeout(() => { try { conn.close(); } catch { /* noop */ } }, 300);
        return;
      }
      // Un invitado se une: añade al roster (si hay hueco) y difunde.
      if (this.roster.length >= MAX_PLAYERS) { conn.send({ t: 'full' }); return; }
      if (!this.roster.some((p) => p.id === conn.peer)) {
        this.roster.push({ id: conn.peer, name: (msg.name || 'Invitado').slice(0, 16), host: false, avatar: msg.avatar || '🙂' });
      }
      conn.send({ t: 'welcome', youId: conn.peer, hostId: this.selfId, proto: PROTOCOL });
      this._broadcastRoster();
      this._emitRoster();
      this.bus.emit(EVENTS.NET_PEER_JOINED, { id: conn.peer });
      return;
    }
    if (msg.t === 'left') {
      // El invitado abandona/rechaza: quítalo del roster y avisa al anfitrión.
      this._onGuestGone(conn.peer);
      this.bus.emit(EVENTS.NET_CLOSED, { reason: 'opponent-left', name: msg.name });
      return;
    }
    // Mensaje de juego de un invitado -> a los suscriptores (host autoritario).
    this._dispatch(msg, conn.peer);
  }

  _onGuestGone(peerId) {
    this.conns.delete(peerId);
    this.roster = this.roster.filter((p) => p.id !== peerId);
    this._broadcastRoster();
    this._emitRoster();
    this.bus.emit(EVENTS.NET_PEER_LEFT, { id: peerId });
  }

  _broadcastRoster() {
    this._broadcast({ t: 'roster', roster: this.roster });
  }

  // --------------------------------------------------------------------------
  // Invitado
  // --------------------------------------------------------------------------
  async joinRoom(code, name, avatar = '🙂') {
    this.isHost = false;
    this.selfName = name || 'Invitado';
    this.selfAvatar = avatar;
    this.code = (code || '').toUpperCase().trim();
    const opts = await getPeerOptions(); // credenciales TURN (Cloudflare) listas antes de conectar
    return new Promise((resolve, reject) => {
      const peer = new Peer({ ...opts, debug: 1 });
      peer.on('open', () => {
        this.peer = peer;
        this.selfId = peer.id;
        const conn = peer.connect(PREFIX + this.code, { reliable: true });
        this.hostConn = conn;
        let opened = false;
        const to = setTimeout(() => { if (!opened) reject(new Error('timeout')); }, 12000);
        conn.on('open', () => {
          opened = true;
          clearTimeout(to);
          conn.send({ t: 'hello', name: this.selfName, avatar: this.selfAvatar, proto: PROTOCOL });
          resolve({ code: this.code });
        });
        conn.on('data', (msg) => this._onGuestData(msg));
        conn.on('close', () => this.bus.emit(EVENTS.NET_CLOSED, { reason: 'host-left' }));
        conn.on('error', (e) => { if (!opened) { clearTimeout(to); reject(e); } });
      });
      peer.on('disconnected', () => { try { peer.reconnect(); } catch { /* noop */ } });
      peer.on('error', (err) => {
        // Tras conectar, ignora errores tardíos del broker (no romper la partida).
        if (this.hostConn && this.hostConn.open) { this._onError(err); return; }
        if (err.type === 'peer-unavailable') reject(new Error('no-room'));
        else this._onError(err);
      });
    });
  }

  _onGuestData(msg) {
    if (!msg || !msg.t) return;
    if (msg.t === 'full') { this.bus.emit(EVENTS.NET_CLOSED, { reason: 'full' }); return; }
    if (msg.t === 'incompatible') { this.bus.emit(EVENTS.NET_CLOSED, { reason: 'version' }); return; }
    if (msg.t === 'welcome') {
      if (msg.proto !== PROTOCOL) { this.bus.emit(EVENTS.NET_CLOSED, { reason: 'version' }); return; }
      this.selfId = msg.youId; return;
    }
    if (msg.t === 'roster') { this.roster = msg.roster || []; this._emitRoster(); return; }
    if (msg.t === 'start') { this.bus.emit(EVENTS.NET_STARTED, { roster: this.roster, host: false, ...msg }); return; }
    if (msg.t === 'left') { this.bus.emit(EVENTS.NET_CLOSED, { reason: 'opponent-left', name: msg.name }); return; }
    // Mensaje de juego del host.
    this._dispatch(msg, this.selfId);
  }

  // --------------------------------------------------------------------------
  // Mensajería de juego (usada por la fase de sincronización)
  // --------------------------------------------------------------------------
  /** Suscribe un manejador de mensajes de juego. Devuelve función para cancelar. */
  onMessage(fn) { this._msgHandlers.add(fn); return () => this._msgHandlers.delete(fn); }
  _dispatch(msg, fromId) { for (const fn of this._msgHandlers) fn(msg, fromId); }

  /** Invitado -> host. */
  sendToHost(msg) { if (this.hostConn && this.hostConn.open) this.hostConn.send(msg); }
  /** Host -> todos los invitados. */
  _broadcast(msg) { for (const c of this.conns.values()) if (c.open) c.send(msg); }
  /** Envía según el rol: host difunde, invitado manda al host. */
  send(msg) { if (this.isHost) this._broadcast(msg); else this.sendToHost(msg); }

  // --------------------------------------------------------------------------
  // Sala
  // --------------------------------------------------------------------------
  /** Enlace para compartir (abre el juego y auto-une por código). */
  shareLink() {
    const base = `${location.origin}${location.pathname}`;
    return `${base}?join=${this.code}`;
  }

  startGame(payload) {
    if (!this.isHost) return;
    this._broadcast({ t: 'start', ...payload });
    this.bus.emit(EVENTS.NET_STARTED, { roster: this.roster, host: true, ...payload });
  }

  _emitRoster() { this.bus.emit(EVENTS.NET_ROSTER, { roster: this.roster, isHost: this.isHost, selfId: this.selfId }); }
  _onError(err) { this.bus.emit(EVENTS.NET_ERROR, { error: err?.type || String(err) }); }

  /** Avisa al rival de que abandonas/rechazas ANTES de cerrar la conexión, para que le
   *  llegue una alerta clara ("tu rival ha abandonado") en vez de un corte de red genérico. */
  announceLeave() {
    try { this.send({ t: 'left', name: this.selfName }); } catch { /* noop */ }
  }

  leave() {
    try { this.peer?.destroy(); } catch { /* noop */ }
    this.peer = null; this.conns.clear(); this.hostConn = null; this.roster = [];
  }
}
